import { kk } from '../../moika-core/src/i18n/kk';
import { ru } from '../../moika-core/src/i18n/ru';

export const LOCALES = ['ru', 'kk'] as const;
export type Locale = (typeof LOCALES)[number];
export type TranslationKey = keyof typeof ru;

const SHELL_RU = {
  'shell.title': 'The Moika Group',
  'shell.login': 'Вход',
  'shell.login.name': 'Логин',
  'shell.login.password': 'Пароль',
  'shell.login.submit': 'Войти',
  'shell.totp.title': 'Подтверждение входа',
  'shell.totp.hint': 'Введите код из приложения-аутентификатора',
  'shell.totp.submit': 'Подтвердить',
  'shell.totp.sub': 'Для вашей роли включён второй фактор',
  'shell.enroll.title': 'Подключите второй фактор',
  'shell.enroll.sub':
    'Ваша роль административная: вход — по паролю и коду из приложения-аутентификатора',
  'shell.enroll.step':
    'Добавьте ключ в Google Authenticator, Яндекс Ключ или аналог — по ссылке с телефона или вручную — и введите код из приложения',
  'shell.enroll.key': 'Ключ для ручного ввода',
  'shell.enroll.link': 'Открыть в приложении',
  'shell.login.welcome': 'С возвращением',
  'shell.login.sub': 'Войдите, чтобы продолжить работу с сетью моек',
  'shell.login.showPassword': 'Показать пароль',
  'shell.login.hidePassword': 'Скрыть пароль',
  'shell.login.back': 'Войти под другим логином',
  'shell.login.foot': 'Доступ выдаёт администратор сети',
  'shell.login.failed': 'Неверный логин или пароль',
  'shell.totp.failed': 'Код не подошёл. Проверьте время на телефоне и введите новый',
  'shell.hero.tagline': 'Панель управления сетью',
  'shell.hero.title': 'Клиента узнаёт номер машины',
  'shell.hero.text':
    'Камера на въезде читает номер, и карточка клиента открывается у оператора раньше, чем водитель опустит стекло.',
  'shell.hero.f1': 'Идентификация по номеру и история визитов',
  'shell.hero.f2': 'Лояльность, подписки и рассылки',
  'shell.hero.f3': 'Касса, смены и сверка по объектам',
  'shell.logout': 'Выйти',
  'shell.site.all': 'Вся сеть',
  'shell.site.select': 'Объект',
  'shell.nav': 'Разделы',
} as const;

const SHELL_KK: Record<keyof typeof SHELL_RU, string> = {
  'shell.enroll.title': 'Екінші факторды қосыңыз',
  'shell.enroll.sub':
    'Сіздің рөліңіз әкімшілік: кіру — құпиясөз және аутентификатор қосымшасының коды арқылы',
  'shell.enroll.step':
    'Кілтті Google Authenticator, Яндекс Кілт немесе ұқсас қосымшаға қосыңыз — телефоннан сілтеме арқылы немесе қолмен — және қосымшадағы кодты енгізіңіз',
  'shell.enroll.key': 'Қолмен енгізуге арналған кілт',
  'shell.enroll.link': 'Қосымшада ашу',
  'shell.title': 'The Moika Group',
  'shell.login': 'Кіру',
  'shell.login.name': 'Логин',
  'shell.login.password': 'Құпия сөз',
  'shell.login.submit': 'Кіру',
  'shell.totp.title': 'Кіруді растау',
  'shell.totp.hint': 'Аутентификатор қосымшасындағы кодты енгізіңіз',
  'shell.totp.submit': 'Растау',
  'shell.totp.sub': 'Сіздің рөліңіз үшін екінші фактор қосылған',
  'shell.login.welcome': 'Қайта келуіңізбен',
  'shell.login.sub': 'Жуу желісімен жұмысты жалғастыру үшін кіріңіз',
  'shell.login.showPassword': 'Құпия сөзді көрсету',
  'shell.login.hidePassword': 'Құпия сөзді жасыру',
  'shell.login.back': 'Басқа логинмен кіру',
  'shell.login.foot': 'Қолжетімділікті желі әкімшісі береді',
  'shell.login.failed': 'Логин немесе құпия сөз дұрыс емес',
  'shell.totp.failed': 'Код келмеді. Телефондағы уақытты тексеріп, жаңасын енгізіңіз',
  'shell.hero.tagline': 'Желіні басқару панелі',
  'shell.hero.title': 'Клиентті көлік нөмірі таниды',
  'shell.hero.text':
    'Кіреберістегі камера нөмірді оқиды, клиент картасы жүргізуші терезені түсірмей тұрып оператордың экранында ашылады.',
  'shell.hero.f1': 'Нөмір бойынша сәйкестендіру және бару тарихы',
  'shell.hero.f2': 'Адалдық, жазылымдар және таратылымдар',
  'shell.hero.f3': 'Касса, ауысымдар және нысандар бойынша салыстыру',
  'shell.logout': 'Шығу',
  'shell.site.all': 'Бүкіл желі',
  'shell.site.select': 'Нысан',
  'shell.nav': 'Бөлімдер',
};

const DICTIONARIES = {
  ru: { ...ru, ...SHELL_RU },
  kk: { ...ru, ...SHELL_RU, ...kk, ...SHELL_KK },
} as const;

export type ShellKey = TranslationKey | keyof typeof SHELL_RU;
export type Translate = (key: ShellKey, params?: Record<string, string | number>) => string;

export function makeT(locale: Locale): Translate {
  const dict = DICTIONARIES[locale] as Record<string, string>;
  return (key, params) => {
    const template = dict[key] ?? key;
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
      params[name] === undefined ? match : String(params[name]),
    );
  };
}

export function translateErrorCode(locale: Locale, code: string): string {
  const dict = DICTIONARIES[locale] as Record<string, string>;
  return dict[`errors.${code}`] ?? code;
}
